import { CampaignChannel } from '~/modules/retailMedia/constants/campaignChannel'
import { deserializeKeywords } from '~/modules/retailMedia/helpers/keywords'

import {
  DTOFormatDestinations,
  mapAgeRestrictedSetting,
  mapToCampaignProducts,
  handleSubpublishers
} from '../../../helpers'

/**
 * Cost factor ("Fator de Custo (%)", super-admin only): maps the raw percentage
 * (1..100) to the `markup_factor` fraction (0 < f <= 1). Returns an empty object
 * when the field was not rendered/filled, so the key is omitted for the
 * non-super-admin path (spread into the create body).
 */
const markupFactorPayload = (
  value: string | number | undefined | null
): { markup_factor?: number } => {
  if (value === undefined || value === null || value === '') return {}

  return { markup_factor: Number(value) / 100 }
}

const resolveDestinationPublishers = (
  publisher_ids: DTOCreateOffsiteBannerCampaignPayload['publisher_ids']
): string[] => {
  if (!publisher_ids) return []

  return Array.isArray(publisher_ids) ? publisher_ids : [publisher_ids]
}

/**
 * Demographic/geographic + interests segmentation (FR-017, FR-017f).
 * Omitted entirely when nothing was filled, same as `channel` — the legacy
 * flow doesn't render AudienceTargeting at all, so `data.audienceTargeting`
 * is undefined there.
 */
const mapAudienceTargeting = (
  audienceTargeting: DTOCreateOffsiteBannerCampaignPayload['audienceTargeting']
) => {
  const { region, ageRange, socialClass, gender, interests } =
    audienceTargeting || {}

  return {
    ...(region?.length ? { region } : {}),
    ...(ageRange?.length ? { age_range: ageRange } : {}),
    ...(socialClass?.length ? { social_class: socialClass } : {}),
    ...(gender?.length ? { gender } : {}),
    ...(interests?.length ? { interests } : {})
  }
}

/**
 * Título, descrição e chamada para ação por criativo, exigidos pela Meta.
 * Só campos preenchidos: vazio não vira chave no corpo.
 */
const mapCreativeCopy = (
  item: DTOCreateOffsiteBannerCampaignPayload['banners'][number]
) => {
  const headline = item.headline?.trim()
  const description = item.description?.trim()
  const callToAction = item.call_to_action?.trim()

  return {
    ...(headline ? { headline } : {}),
    ...(description ? { description } : {}),
    ...(callToAction ? { call_to_action: callToAction } : {})
  }
}

const resolveBudget = (data: DTOCreateOffsiteBannerCampaignPayload) => {
  const { lengthCentsCurrency } = data

  const toAmount = (raw: string | undefined) =>
    lengthCentsCurrency ? Number(raw) / 100 : Number(raw)

  return {
    total_budget: toAmount(data?.total_budget?.numAsString),
    daily_budget: toAmount(data.budget?.daily?.numAsString),
    cpm: toAmount(data.budget?.cpm?.numAsString)
  }
}

export const DTOCreateOffsiteBannerCampaign = (
  data: DTOCreateOffsiteBannerCampaignPayload
) => {
  const costType: DTOCreateBannerCampaignCostType =
    (data?.cost_type as DTOCreateBannerCampaignCostType) || 'standard'

  const destination_publishers = resolveDestinationPublishers(
    data?.publisher_ids
  )

  /**
   * Handle banner config
   */
  const bannerData: DTOCreateBannerCampaignConfig = {
    keywords: deserializeKeywords(data.targeting?.keywords),
    categories: data.targeting?.categories?.split(',') || null,
    assets: data.banners.map(item => ({
      ad_size: item.ad_size,
      alt_text: item.alt_text,
      media_url: item.media_url,
      // Per-creative destination URL (FR-022f a FR-022i), falling back to
      // the campaign's own default URL when the creative has none of its
      // own — never zeroed out, since the campaign default may itself be
      // absent (e.g. multi-brand destinations).
      destination_url: item.destination_url || data.settings?.destination_url,
      ...(data.channel === CampaignChannel.META ? mapCreativeCopy(item) : {})
    }))
  }

  if (data.settings?.destination_url) {
    const destinations = destination_publishers.map(publisher_id => {
      return {
        publisher_id,
        url: data.settings?.destination_url
      } as DestinationUrl
    })
    bannerData.destinations = destinations
  }

  if (data.settings?.destinations) {
    bannerData.destinations = DTOFormatDestinations(data.settings?.destinations)
  }

  /**
   * Handle products linkded to campaign
   */
  let products = mapToCampaignProducts(data?.products || [])

  const hasSingleDestinationPublisher = destination_publishers.length === 1
  if (hasSingleDestinationPublisher) {
    products = products?.map(product => ({
      ...product,
      sub_publisher_id: destination_publishers[0]
    }))
  }

  /*
   * Handle budget
   */
  const budget = resolveBudget(data)

  const costsByType = {
    standard: () => ({
      cost_type: 'standard' as const,
      daily_budget: budget.daily_budget,
      cpm: budget.cpm
    }),
    total_budget: () => ({
      cost_type: 'total_budget' as const,
      total_budget: budget.total_budget
    })
  }

  /**
   * Handle campaign settings
   */

  const audienceTargeting = mapAudienceTargeting(data?.audienceTargeting)

  const settings: DTOCreateBannerCampaignSettings = {
    products,
    ...(costType === 'standard' ? costsByType.standard() : {}),
    ...mapAgeRestrictedSetting(data?.settings),
    // Channel branches on the data, not on the permission that gates the
    // step rendering it — omitted entirely when absent (FR-015, FR-002a).
    ...(data.channel ? { channel: data.channel } : {}),
    // Objective is only meaningful alongside a channel (FR-027b, FR-015a).
    ...(data.channel && data.investmentObjectiveType
      ? { investment_objective_type: data.investmentObjectiveType }
      : {}),
    // Frozen Publya estimate snapshot, captured at simulation time by
    // Projections — not a registered unform field, lifted directly into
    // preData by Offsite/Banner/index.tsx (FR-038).
    ...(data.projections ? { projections: data.projections } : {}),
    ...(Object.keys(audienceTargeting).length
      ? { audience_targeting: audienceTargeting }
      : {}),
    // Vínculo do CSV já enviado ao armazenamento (FR-052j): chave, nome original e
    // instante. É por ele que o domínio localiza o arquivo para anexá-lo ao pedido
    // — o conteúdo nunca passa por este corpo. Também não é campo do unform: o
    // AudienceImport o eleva pelo contexto, como Projections faz.
    ...(data.audienceFile ? { audience_file: data.audienceFile } : {})
  }

  if (data?.placements?.placementsCheck === 'selected') {
    const placements = data?.placements?.placements_ids
    const selectedPlacements = Object.values(placements).filter(
      values => values.checked
    )

    settings.placements = selectedPlacements.map(({ value: id }) => ({ id }))
  }

  if (data?.settings?.budget_type) {
    settings.budget_type = data.settings.budget_type
  }

  let body: DTOCreateOffsiteBannerCampaignResponse = {
    publisher_id: data?.publisher_id,

    // Cost factor ("Fator de Custo (%)", super-admin only): percentage -> fraction,
    // sent at the top level, omitted when the field was not rendered/filled.
    ...markupFactorPayload(data?.markup_factor),

    ...(costType === 'total_budget' ? costsByType.total_budget() : {}),

    audience_id: data?.audience
      ? (Number(data.audience) as AudienceData['id'])
      : null,

    name: data.name,
    description: data?.description,

    type: 'off_site',
    ad_type: 'banner_off_site',

    start_at: data.start_date,
    end_at: data?.end_date || null,

    banner: { ...bannerData },

    settings,

    destination_publishers
  }

  body = handleSubpublishers<DTOCreateOffsiteBannerCampaignResponse>({
    body,
    data
  })

  return body
}
